import type { ReactNode } from 'react';
import type { ImageField, LinkField, RichTextField } from '@prismicio/client';
import { isFilled } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { PrismicRichText } from '@prismicio/react';
import { PrimaryGreenCtaButton } from '@/components/common/PrimaryGreenCtaButton';
import { cn } from '@/lib/utils';
import { ScrollRail } from './ScrollRail';
import { SplitColorTitle, type SplitColorTitleLayout } from './SplitColorTitle';

export { ScrollRail } from './ScrollRail';
export { SplitColorTitle, type SplitColorTitleLayout } from './SplitColorTitle';

type Theme = 'light' | 'dark' | null;

/**
 * Vertical spacing between the header column's title / summary / CTA stack.
 * `md` is the default (24px); `lg` (36px) is what the carousel variant asks
 * for — kept as a token rather than a raw className so both existing and
 * future variants pick from a small set of intentional values.
 */
export type SideHeaderLeftGap = 'md' | 'lg';

/**
 * Desktop width of the header column. `default` (45%) keeps the original
 * layout; `wide` (70%) gives the header more room and lets the right column
 * present a narrower carousel viewport. Mobile / tablet stay stacked and
 * full width regardless.
 */
export type SideHeaderLeftWidth = 'default' | 'wide';

export type SideHeaderCardsRowProps = {
  sliceType: string;
  variation: string;
  theme?: Theme;
  backgroundImage?: ImageField | null;
  /** Optional logo shown above the title in the header column. */
  logo?: ImageField | null;
  titleLead?: RichTextField | null;
  titleBody?: RichTextField | null;
  titleLayout: SplitColorTitleLayout;
  summary?: RichTextField | null;
  primaryCta?: LinkField | null;
  /** Vertical spacing between title / summary / CTA. Defaults to `md` (24px). */
  leftGap?: SideHeaderLeftGap;
  /** Desktop width of the header column. Defaults to `default` (45%). */
  leftWidth?: SideHeaderLeftWidth;
  /**
   * Right-side content. When omitted, the default `ScrollRail` wraps
   * `children` — this preserves the default variant's API. Variants that need
   * a different right-side treatment (e.g. a carousel) render it themselves
   * here and pass `children` as `null`.
   */
  rightSlot?: ReactNode;
  /** Cards for the default `ScrollRail`; ignored when `rightSlot` is provided. */
  children?: ReactNode;
};

const LEFT_GAP_CLASSES: Record<SideHeaderLeftGap, string> = {
  md: 'gap-6',
  lg: 'gap-9',
};

// Mobile / tablet keep the header column full width (the layout is stacked
// until `lg`), so the width token only applies from `lg` up.
const LEFT_WIDTH_CLASSES: Record<SideHeaderLeftWidth, string> = {
  default: 'lg:w-[45%]',
  wide: 'lg:w-[53%]',
};

/**
 * Section shell for a family of "left header, right cards" slices: the header
 * column (optional logo, title, summary, primary CTA) stays put while the
 * cards column presents the content — a horizontal scroll rail by default,
 * or whatever the variant hands in via `rightSlot`.
 */
export function SideHeaderCardsRow({
  sliceType,
  variation,
  theme,
  backgroundImage,
  logo,
  titleLead,
  titleBody,
  titleLayout,
  summary,
  primaryCta,
  leftGap = 'md',
  leftWidth = 'default',
  rightSlot,
  children,
}: SideHeaderCardsRowProps) {
  const isDark = theme === 'dark';
  const resolvedTheme: 'light' | 'dark' = isDark ? 'dark' : 'light';
  const hasSummary = isFilled.richText(summary);
  const hasLogo = isFilled.image(logo);

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={variation}
      className={cn(
        'relative overflow-hidden px-5 py-16 md:px-[30px] md:py-20 lg:py-[70px]',
        isDark ? 'bg-[#262626]' : 'bg-white text-[#041118]',
      )}
    >
      {isFilled.image(backgroundImage) && (
        <PrismicNextImage
          field={backgroundImage}
          fill
          sizes="100vw"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
          fallbackAlt=""
        />
      )}

      <div className="relative mx-auto flex w-full max-w-[1280px] flex-col gap-10 lg:flex-row lg:items-center lg:gap-10">
        <div
          className={cn(
            'flex flex-col lg:shrink-0',
            LEFT_WIDTH_CLASSES[leftWidth],
            LEFT_GAP_CLASSES[leftGap],
          )}
        >
          {hasLogo && (
            <PrismicNextImage
              field={logo}
              className="h-auto w-auto max-w-[320px] object-contain"
              fallbackAlt=""
            />
          )}
          <SplitColorTitle
            lead={titleLead}
            body={titleBody}
            layout={titleLayout}
            theme={resolvedTheme}
          />
          {hasSummary && (
            <PrismicRichText
              field={summary}
              components={{
                paragraph: ({ children: paragraphChildren }) => (
                  <p
                    className={cn(
                      'text-[16px] font-light leading-[1.5] md:text-[18px]',
                      isDark ? 'text-white/75' : 'text-[#041118]/75',
                    )}
                  >
                    {paragraphChildren}
                  </p>
                ),
              }}
            />
          )}
          {isFilled.link(primaryCta) && (
            <div>
              <PrimaryGreenCtaButton field={primaryCta} />
            </div>
          )}
        </div>

        {/*
          Right column: `min-w-0` keeps the flex child from stretching to fit
          its horizontally scrolling content — without it, the rail would push
          the whole row wider than the section and force page-level overflow.
        */}
        <div className="min-w-0 flex-1">
          {rightSlot ?? <ScrollRail>{children}</ScrollRail>}
        </div>
      </div>
    </section>
  );
}
